// src/store/finance.js
import axios from '@axios'

const defaultPagination = {
  page: 1, per_page: 10, total_objects: 0, total_pages: 1,
}
const defaultTotals = { amount_without_commission: 0 }

const state = () => ({
  // ===== CxP =====
  payables: {
    meta: {
      // Hoy el endpoint solo regresa count; dejamos estos campos para compatibilidad en UI
      pagination: { ...defaultPagination },
      totals: { ...defaultTotals }, // reservado por si luego agregas summary en backend
      count: 0,
    },
    data: [],
  },

  // ===== CxC =====
  receivables: {
    data: [],
    summary: {
      total: 0, al_corriente: 0, a_1_15: 0, a_16_30: 0, a_30_mas: 0, vencido: 0,
    },
    customers: [],
    projects: [],
    creditHeader: null,
  },

  dashboard: {
    payables: { total: 0, rows: [] },
    receivables: { total: 0, rows: [] },
  },
})

const getters = {
  // ===== CxP =====
  payables: s => s.payables.data,
  payablesCount: s => Number(s.payables.meta?.count ?? 0),

  // compat: si alguna vista aún lee pagination/totals
  payablesPagination: s => s.payables.meta.pagination,
  payablesTotals: s => s.payables.meta?.totals || { ...defaultTotals },

  // ===== CxC =====
  receivables: s => s.receivables.data,
  receivablesSummary: s => s.receivables.summary,
  receivablesCustomers: s => s.receivables.customers,
  receivablesProjects: s => s.receivables.projects,
  receivablesCreditHeader: s => s.receivables.creditHeader,

  payablesDashboard: s => s.dashboard.payables,
  receivablesDashboard: s => s.dashboard.receivables,
}

const mutations = {
  // ===== CxP =====
  SET_PAYABLES(state, payload) {
    const data = Array.isArray(payload?.data) ? payload.data : []
    const count = Number(payload?.meta?.count ?? 0)

    state.payables.data = data
    state.payables.meta.count = count

    // compat con tablas que leen pagination
    const pag = state.payables.meta.pagination
    pag.total_objects = count
    pag.total_pages = Math.max(1, Math.ceil(count / (pag.per_page || 10)))
  },
  CHANGE_PAGINATION(state, { field, value }) {
    state.payables.meta.pagination[field] = value
  },
  SET_TOTALS(state, totals) {
    state.payables.meta.totals = totals || { ...defaultTotals }
  },
  CLEAR_PAYABLES(state) {
    state.payables = {
      meta: {
        pagination: { ...defaultPagination },
        totals: { ...defaultTotals },
        count: 0,
      },
      data: [],
    }
  },

  // ===== CxC =====
  SET_RECEIVABLES(state, rows) { state.receivables.data = rows || [] },
  SET_RECEIVABLES_SUMMARY(state, summary) { state.receivables.summary = summary || {} },
  SET_RECEIVABLES_CUSTOMERS(state, customers) { state.receivables.customers = customers || [] },
  SET_RECEIVABLES_PROJECTS(state, projects) { state.receivables.projects = projects || [] },
  SET_RECEIVABLES_CREDIT_HEADER(state, header) { state.receivables.creditHeader = header || null },

  // ===== Dashboards =====
  SET_PAYABLES_DASHBOARD(state, payload) {
    state.dashboard.payables = {
      total: Number(payload?.total ?? 0),
      rows: Array.isArray(payload?.rows)
        ? payload.rows.map(x => ({ id: x.id, name: x.name, balance: Number(x.balance ?? 0) }))
        : [],
    }
  },
  SET_RECEIVABLES_DASHBOARD(state, payload) {
    state.dashboard.receivables = {
      total: Number(payload?.total ?? 0),
      rows: Array.isArray(payload?.rows)
        ? payload.rows.map(x => ({ id: x.id, name: x.name, balance: Number(x.balance ?? 0) }))
        : [],
    }
  },
}

const actions = {
  // ===================== CxP =====================
  /**
   * Carga CxP desde /v1/payables
   * params soportados: { q, unpaid_only, supplier_branch_id, project_id, sold_from, sold_to }
   */
  async fetchPayables({ commit }, params = {}) {
    const query = {
      ...(params.q ? { q: params.q } : {}),
      ...(params.unpaid_only ? { unpaid_only: true } : {}),
      ...(params.only_overdue ? { only_overdue: true } : {}),
      ...(params.supplier_id ? { supplier_id: params.supplier_id } : {}),
      ...(params.supplier_branch_id ? { supplier_branch_id: params.supplier_branch_id } : {}),
      ...(params.project_id ? { project_id: params.project_id } : {}),
      ...(params.sold_from ? { sold_from: params.sold_from } : {}),
      ...(params.sold_to ? { sold_to: params.sold_to } : {}),
    }

    const { data } = await axios.get('/v1/payables', { params: query })
    const payload = data || { data: [], meta: { count: 0 } }
    // Hotfix: filtra en cliente si se pidió sólo vencidas
    if (params.only_overdue) {
      payload.data = (payload.data || []).filter(r => !r.paid_in_full && Number(r.days_overdue) >= 0)
      payload.meta = { ...(payload.meta || {}), count: payload.data.length }
    }

    commit('SET_PAYABLES', payload)
    return payload
  },

  async revertPurchaseOrderPayment(_, { poId }) {
    const { data } = await axios.post(`/v1/purchase_orders/${poId}/revert_payment`)
    return data
  },

  // Guardar número de factura en la OC
  async setPurchaseOrderInvoice(_, { poId, invoiceNumber }) {
    const { data } = await axios.patch(`/v1/purchase_orders/${poId}`, {
      purchase_order: { invoice_number: (invoiceNumber || '').toString().trim() || null },
    })
    return data
  },

  // Actualizar plazo (term_days) de la OC (recalcula due_date en backend)
  async setPurchaseOrderTermDays(_, { poId, termDays }) {
    const { data } = await axios.patch(`/v1/purchase_orders/${poId}`, {
      purchase_order: { term_days: Number(termDays) },
    })
    return data
  },

  // Pago parcial
  async payPurchaseOrderPartial(_, { poId, amount }) {
    const { data } = await axios.post(`/v1/purchase_orders/${poId}/pay`, { amount: Number(amount) })
    return data
  },

  // Pago total (saldo completo)
  async payPurchaseOrderFull(_, { poId }) {
    const { data } = await axios.post(`/v1/purchase_orders/${poId}/mark_paid_in_full`)
    return data
  },

  // ===== (opcionales) Dashboard / buckets si existen en backend =====
  async fetchPayablesDashboard({ commit }) {
    try {
      const { data } = await axios.get('/v1/payables/dashboard')
      commit('SET_PAYABLES_DASHBOARD', data || {})
      return data
    } catch (e) {
      if (e?.response?.status === 404) {
        commit('SET_PAYABLES_DASHBOARD', { total: 0, rows: [] })
        return { total: 0, rows: [] }
      }
      throw e
    }
  },

  async fetchPayablesFridayBuckets(_, { month } = {}) {
    try {
      const { data } = await axios.get('/v1/payables/friday_buckets', {
        params: { month, _: Date.now() },
      })
      return data || {}
    } catch (e) {
      if (e?.response?.status === 404) return {}
      throw e
    }
  },

  // ===================== CxC (igual que ya tenías) =====================
  async fetchReceivables({ commit }, params = {}) {
    const { data } = await axios.get('/v1/receivables', { params })
    commit('SET_RECEIVABLES', data?.data || [])
    return data
  },

  async fetchReceivablesSummary({ commit }, params = {}) {
    const { data } = await axios.get('/v1/receivables/summary', { params })
    commit('SET_RECEIVABLES_SUMMARY', data || {})
    return data
  },

  async fetchReceivablesCustomers({ commit }) {
    const { data } = await axios.get('/v1/receivables/customers')
    commit('SET_RECEIVABLES_CUSTOMERS', data || [])
    return data
  },

  async fetchReceivablesProjects({ commit }, { customer_id } = {}) {
    const { data } = await axios.get('/v1/receivables/projects', { params: { customer_id } })
    commit('SET_RECEIVABLES_PROJECTS', data || [])
    return data
  },

  async fetchReceivablesCreditHeader({ commit }, { customer_id }) {
    const { data } = await axios.get('/v1/receivables/credit_header', { params: { customer_id } })
    commit('SET_RECEIVABLES_CREDIT_HEADER', data || null)
    return data
  },

  async exportReceivablesCsv(_, params = {}) {
    return axios.get('/v1/receivables/export_csv', { params, responseType: 'blob' })
  },

  // acciones receivables existentes
  async markPaid(_, { quoteId }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/mark_paid`)
    return data
  },
  async payQuotePartial(_, { quoteId, amount }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/pay`, { amount: Number(amount) })
    return data
  },

  async unmarkPaid(_, { quoteId }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/unmark_paid`)
    return data
  },

  async setQuoteCheckplusReference(_, { quoteId, checkplusReference }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/set_checkplus_reference`, { checkplus_reference: checkplusReference })
    return data
  },

  async clearQuoteCheckplusReference(_, { quoteId }) {
    const { data } = await axios.delete(`/v1/quotes/${quoteId}/clear_checkplus_reference`)
    return data
  },

  async issueQuoteFacturapiInvoice(_, { quoteId, paymentForm, series }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/issue_facturapi_invoice`, {
      payment_form: paymentForm,
      series,
    })
    return data
  },

  async previewQuoteFacturapiInvoice(_, { quoteId, paymentForm, series }) {
    return axios.post(`/v1/quotes/${quoteId}/preview_facturapi_invoice`, {
      payment_form: paymentForm,
      series,
    }, {
      responseType: 'blob',
    })
  },

  async fetchQuoteFacturapiInvoice(_, { quoteId }) {
    const { data } = await axios.get(`/v1/quotes/${quoteId}/facturapi_invoice`)
    return data
  },

  async downloadQuoteFacturapiInvoice(_, { quoteId, format = 'zip' }) {
    return axios.get(`/v1/quotes/${quoteId}/download_facturapi_invoice`, {
      params: { format },
      responseType: 'blob',
    })
  },

  async cancelQuoteFacturapiInvoice(_, {
    quoteId, motive, substitution, reason,
  }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/cancel_facturapi_invoice`, {
      motive,
      substitution,
      reason,
    })
    return data
  },

  async syncQuoteFacturapiInvoiceCancellation(_, { quoteId }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/sync_facturapi_invoice_cancellation`)
    return data
  },

  async issueQuoteFacturapiPaymentInvoice(_, { quoteId, paymentForm, series }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/issue_facturapi_payment_invoice`, {
      payment_form: paymentForm,
      series,
    })
    return data
  },

  async fetchQuoteFacturapiPaymentInvoice(_, { quoteId }) {
    const { data } = await axios.get(`/v1/quotes/${quoteId}/facturapi_payment_invoice`)
    return data
  },

  async downloadQuoteFacturapiPaymentInvoice(_, { quoteId, format = 'zip' }) {
    return axios.get(`/v1/quotes/${quoteId}/download_facturapi_payment_invoice`, {
      params: { format },
      responseType: 'blob',
    })
  },

  async cancelQuoteFacturapiPaymentInvoice(_, {
    quoteId, motive, substitution, reason,
  }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/cancel_facturapi_payment_invoice`, {
      motive,
      substitution,
      reason,
    })
    return data
  },

  async syncQuoteFacturapiPaymentInvoiceCancellation(_, { quoteId }) {
    const { data } = await axios.post(`/v1/quotes/${quoteId}/sync_facturapi_payment_invoice_cancellation`)
    return data
  },

  async issueReceivablesPaymentComplement(_, {
    customerId, projectId, taxInformationId, quoteIds, quoteAmounts, pendingRepIds, amount, paymentDate, paymentForm, series, saveUnallocated,
  }) {
    const { data } = await axios.post('/v1/receivables/payment_complement', {
      customer_id: customerId,
      project_id: projectId,
      tax_information_id: taxInformationId,
      quote_ids: quoteIds,
      quote_amounts: quoteAmounts,
      pending_rep_ids: pendingRepIds,
      amount: Number(amount),
      payment_date: paymentDate,
      payment_form: paymentForm,
      series,
      save_unallocated: saveUnallocated,
    })
    return data
  },

  async fetchReceivablesPaymentComplements(_, { customerId, taxInformationId } = {}) {
    const { data } = await axios.get('/v1/receivables/payment_complements', {
      params: {
        customer_id: customerId,
        tax_information_id: taxInformationId,
      },
    })
    return data
  },

  async cancelReceivablesPaymentComplement(_, {
    repId, motive, substitution, reason,
  }) {
    const { data } = await axios.post(`/v1/receivables/payment_complements/${repId}/cancel`, {
      motive,
      substitution,
      reason,
    })
    return data
  },

  async syncReceivablesPaymentComplementCancellation(_, { repId }) {
    const { data } = await axios.post(`/v1/receivables/payment_complements/${repId}/sync_cancellation`)
    return data
  },

  async voidReceivablesPendingPayment(_, { repId, reason }) {
    const { data } = await axios.post(`/v1/receivables/payment_complements/${repId}/void`, {
      reason,
    })
    return data
  },

  async fetchReceivablesDashboard({ commit }) {
    try {
      const { data } = await axios.get('/v1/receivables/dashboard')
      commit('SET_RECEIVABLES_DASHBOARD', data || {})
      return data
    } catch (e) {
      if (e?.response?.status === 404) {
        commit('SET_RECEIVABLES_DASHBOARD', { total: 0, rows: [] })
        return { total: 0, rows: [] }
      }
      throw e
    }
  },

  async fetchReceivablesFridayBuckets(_, { month } = {}) {
    try {
      const { data } = await axios.get('/v1/receivables/friday_buckets', {
        params: { month, _: Date.now() },
      })
      return data || {}
    } catch (e) {
      if (e?.response?.status === 404) return {}
      throw e
    }
  },
}

export default {
  namespaced: true, state, getters, mutations, actions,
}
